import { h, ComponentChildren, RefObject } from "preact";
import { useEffect, useRef } from "preact/hooks";

import { Keyboard } from "../../../foundation/enums";
import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";
import { stopPropagation } from "../../../foundation/utils/legacy";
import Portal from "../../Portal";
import Button, { ButtonProps, ButtonVariant } from "../Button";

import styles from "./Dialog.module.css";

interface DialogButtonProps extends ButtonProps {
    dismissOnClick?: boolean;
    disableEnterShortcut?: boolean;
}

interface Props {
    containerClass?: string;
    class?: string;
    childrenClass?: string;
    style?: CSSProperties;
    title?: ComponentChildren;
    description?: ComponentChildren;
    children?: ComponentChildren;
    primaryButtonProps?: DialogButtonProps;
    secondaryButtonProps?: DialogButtonProps;
    tertiaryButtonProps?: DialogButtonProps;
    isDismissible?: boolean;
    isFooterHidden?: boolean;
    hasBackdropColor?: boolean;
    dialogRef?: RefObject<HTMLDivElement>;
    onClose?: () => void;
}

function Dialog({
    containerClass,
    class: cssClass,
    childrenClass,
    style,
    title,
    description,
    children,
    primaryButtonProps,
    secondaryButtonProps,
    tertiaryButtonProps,
    isDismissible = true,
    isFooterHidden,
    hasBackdropColor = true,
    dialogRef,
    onClose
}: Props) {
    const primaryButtonRef = useRef<HTMLButtonElement | null>(null);

    function handlePrimaryButtonRef(el: HTMLButtonElement | null) {
        const { buttonRef } = primaryButtonProps ?? {};

        if (typeof buttonRef === "function") {
            buttonRef(el);
        } else if (buttonRef) {
            buttonRef.current = el;
        }

        primaryButtonRef.current = el;
    }

    // Enter shortcut
    useEffect(() => {
        if (
            !primaryButtonProps ||
            primaryButtonProps.disabled ||
            primaryButtonProps.disableEnterShortcut ||
            !primaryButtonRef.current
        ) {
            return;
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.code === Keyboard.Key.Enter) {
                primaryButtonRef.current!.click();

                event.stopPropagation();
                event.preventDefault();
            }
        }

        document.addEventListener("keydown", handleKeyDown, true);

        return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, [primaryButtonProps]);

    // Escape shortcut
    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if (event.code === Keyboard.Key.Escape) {
                onClose?.();

                event.stopPropagation();
            }
        }

        if (isDismissible) {
            document.addEventListener("keydown", handleKeyDown, true);
        }

        return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, [isDismissible, onClose]);

    function renderButton(
        consumerProps: DialogButtonProps | undefined,
        componentProps: Partial<Omit<ButtonProps, "onClick">>
    ) {
        if (!consumerProps) {
            return null;
        }

        const { class: consumerClass, onClick, dismissOnClick = true, ...restOfConsumerProps } = consumerProps;
        const { class: componentClass, ...restOfComponentProps } = componentProps;

        const props: ButtonProps = {
            ...restOfComponentProps,
            ...restOfConsumerProps,
            class: classname(componentClass, consumerClass),
            onClick: (event: MouseEvent) => {
                onClick?.(event);
                if (dismissOnClick) {
                    onClose?.();
                }
            }
        };

        return <Button {...props}/>;
    }

    function renderTitle() {
        if (typeof title === "string") {
            return (
                <span class={classname(styles.title, "body-1 medium")}>{title}</span>
            );
        }

        return title;
    }

    return (
        <Portal>
            <div class={classname(styles.dialogContainer, containerClass)} onMouseDown={stopPropagation}>
                <div
                    class={classname(styles.backdrop, { [styles.hasBackdropColor]: hasBackdropColor })}
                    onClick={isDismissible ? onClose : undefined}/>
                <div
                    class={classname(styles.dialog, cssClass)}
                    ref={dialogRef}
                    style={style}
                    onClick={stopPropagation}>
                    {renderTitle()}
                    {!!description && <span class={classname(styles.description, "body-1")}>{description}</span>}
                    {children && <div class={classname(styles.children, childrenClass, "body-1")}>{children}</div>}
                    {!isFooterHidden && (
                        <div class={styles.footer}>
                            {tertiaryButtonProps && (
                                <div class={styles.tertiaryButtonContainer}>
                                    {renderButton(tertiaryButtonProps, { variant: ButtonVariant.Ninja })}
                                </div>
                            )}
                            {renderButton(secondaryButtonProps, { variant: ButtonVariant.Secondary })}
                            {renderButton(primaryButtonProps, {
                                variant: ButtonVariant.Primary,
                                buttonRef: handlePrimaryButtonRef
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    );
}

export default Dialog;
export {
    Props as DialogProps
};
